/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ActionConfig,
  ActionReference,
  Member,
  Team,
  TriggerConfig,
  TriggerDescription,
  TriggerReference,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import type { Fields } from "@smartsuite/smartsuite-client";
import * as R from "remeda";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../types/candidate";
import { getOutputDescriptions } from "../../../../../utils/conditions-coverter.hook";
import {
  ActionLabel,
  CURRENT_ITEM_OUTPUT_ID,
} from "../../../../../utils/constants";
import {
  generalTypeFilter,
  isSimpleTextOutput,
  isSupportedOutput,
  nameToInitials,
  ssTypeFilter,
  systemTypeFilter,
} from "../utils";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import { usePillTooltip } from "../../../../../utils/make-tooltip.hook";
import { findMatchedAction } from "../../../../../utils/match-action";
import type { LoopScope } from "../../../../../stores/automation/looping.store";
import { t } from "i18next";
import { rootStore } from "../../../../../stores";
import { currentItemOutputsRefPill } from "../utils/current-item-outputs-ref-pill";
import type { ActionOptionGroup } from "../utils/action-options";

export interface TriggerActionOption {
  title: string;
  outputId: string;
  icon?: string;
  contextSpecific: boolean;
}

export interface LoopingOptionGroup {
  name: string;
  options: TriggerActionOption[];
}

interface MemberPill extends FieldTypePillProps {
  type: "actionOutput" | "triggerOutput" | "member" | "team" | "loop";
  value: string;
}

export const triggerToOptions = (
  triggerDescription: TriggerDescription,
  generalTypes?: ValueDescription_T[],
  ssTypes?: ValueDescription_T[],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): TriggerActionOption[] =>
  (triggerDescription.recordListOutput !== undefined
    ? triggerDescription.recordListOutput.fields.concat(
        triggerDescription.outputs,
      )
    : triggerDescription.outputs
  )
    .filter((output) => {
      if (isSimpleTextOutput(output)) {
        return true;
      }
      if (output.contextSpecific) {
        return systemTypeFilter(systemAllowedOptions, output);
      }
      if (output.smartsuiteFieldType !== undefined) {
        return ssTypeFilter(
          output.smartsuiteFieldType,
          ssFieldTypes,
          ssTypes,
          output,
        );
      }

      return generalTypeFilter(generalTypes, output);
    })
    .map(
      (output): TriggerActionOption => ({
        title:
          output.label +
          (output.labelSuffix === undefined ? "" : ": " + output.labelSuffix),
        outputId: output.outputId,
        icon: output.icon,
        contextSpecific: output.contextSpecific,
      }),
    );

interface ConvertersFunctions {
  convertInputToPills: (
    input: InputCandidate,
    members: Member[],
    teams: Team[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    triggerOptions: TriggerActionOption[],
    actionOptions: ActionOptionGroup[],
    currentAction?: ActionCandidate,
    loopingFields?: LoopingOptionGroup,
  ) => MemberPill[];
}

export const useConverters = (): ConvertersFunctions => {
  const {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
  } = usePillTooltip();

  const convertInputToPills = (
    input: InputCandidate,
    members: Member[],
    teams: Team[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    triggerOptions: TriggerActionOption[],
    actionOptions: ActionOptionGroup[],
    currentAction?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ): MemberPill[] =>
    input.value?.values.flatMap((value): MemberPill[] => {
      const rootLooping = rootStore.automations.candidate.looping;
      const looping = currentAction
        ? (rootLooping?.scopeForAction?.(currentAction.hash) ?? rootLooping)
        : rootLooping;
      const trigger = rootStore.automations.candidate.self.trigger;

      switch (value.value.oneofKind) {
        case "string": {
          const memberId = value.value.string;
          const member = R.pipe(
            members,
            R.find((member) =>
              R.isTruthy(member.name)
                ? member.name === memberId
                : member.email[0] === memberId,
            ),
          );
          const title = R.isTruthy(member)
            ? R.isTruthy(member.name)
              ? member.name
              : member.email[0]
            : "";
          return [
            {
              type: "member",
              value: memberId,
              avatar: {
                initials: nameToInitials(title),
                image: member?.profileImageUrl,
              },
              title,
            },
          ];
        }
        case "reference":
          if (value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID) {
            return [
              {
                type: "loop",
                value: JSON.stringify(value.value.reference),
                icon: "",
                title: t("general.current-item"),
                tooltip: makeCurrentItemPillTooltip(looping),
              },
            ];
          }
          if (value.value.reference.source.oneofKind === "triggerReference") {
            const { outputId } = value.value.reference;

            let option = triggerOptions.find(
              (option) => option.outputId === outputId,
            );

            if (option === undefined && loopOptions?.options.length) {
              option = loopOptions.options.find(
                (option) => option.outputId === outputId,
              );
            }

            return [
              {
                type: "triggerOutput",
                value: outputId,
                icon: option?.icon ?? "",
                title:
                  option?.title ??
                  String(t("automation-form.deleted-field-title")),
                isError: !option?.title,
                tooltip: makeTriggerPillTooltip(trigger),
              },
            ];
          } else if (
            value.value.reference.source.oneofKind === "actionReference"
          ) {
            const { outputId, source } = value.value.reference;
            let actionOption = findActionOption(
              actionOptions,
              source.actionReference.actionId,
              source.actionReference.instanceId,
              outputId,
            );

            if (actionOption === undefined && loopOptions?.options.length) {
              actionOption = loopOptions.options.find(
                (option) => option.outputId === outputId,
              );
            }

            const pillAction = findMatchedAction(
              value.value.reference.source,
              actions,
            );

            return [
              {
                type: "actionOutput",
                value: outputId,
                icon: actionOption?.icon ?? "",
                title:
                  actionOption?.title ??
                  String(t("automation-form.deleted-field-title")),
                isError:
                  pillAction === undefined
                    ? true
                    : !isSupportedOutput(
                        pillAction,
                        actionOptions.map((option) => option.action),
                        currentAction,
                        looping,
                      ),
                tooltip:
                  pillAction === undefined
                    ? undefined
                    : makeActionPillTooltip(pillAction),
              },
            ];
          }
          return [];
        case "function":
          if (value.value.function.function.oneofKind === "teamMemberIds") {
            const { teamId } = value.value.function.function.teamMemberIds;
            const team = teams.find((team) => team.teamId === teamId);
            const title = team?.name ?? "Unknown Team";
            return [
              {
                type: "team",
                value: teamId,
                ...(team === undefined
                  ? {}
                  : { avatar: { initials: nameToInitials(title) } }),
                title,
                isError: team === undefined,
              },
            ];
          }
          if (value.value.function.function.oneofKind === "memberName") {
            const memberId = value.value.function.function.memberName.memberId;
            const member = R.pipe(
              members,
              R.find((member) => {
                return member.memberId === memberId;
              }),
            );
            const title = R.isTruthy(member)
              ? R.isTruthy(member.name)
                ? member.name
                : member.email[0]
              : "Unknown User";

            return [
              {
                type: "member",
                value: memberId,
                ...(member === undefined
                  ? {}
                  : {
                      avatar: {
                        initials: nameToInitials(title),
                        image: member.profileImageUrl,
                      },
                    }),
                title,
                isError: member === undefined,
              },
            ];
          }
          return [];
        default:
          return [];
      }
    }) ?? [];

  return {
    convertInputToPills,
  };
};

export const mergeNextPillToInput = (
  input: InputCandidate,
  pill:
    | {
        type: "triggerOutput";
        triggerReference: TriggerReference;
        outputId: string;
      }
    | {
        type: "actionOutput";
        actionReference: ActionReference;
        outputId: string;
      }
    | { type: "member"; memberId: string }
    | { type: "team"; teamId: string }
    | { type: "loop"; looping: LoopScope },
  multiple: boolean,
): InputCandidate => ({
  ...input,
  value: {
    values: [
      ...(multiple && R.isTruthy(input.value) ? input.value.values : []),
      ...(() => {
        switch (pill.type) {
          case "triggerOutput":
            return [
              {
                value: {
                  oneofKind: "reference",
                  reference: {
                    source: {
                      oneofKind: "triggerReference",
                      triggerReference: pill.triggerReference,
                    },
                    outputId: pill.outputId,
                  },
                } as const,
              },
            ];
          case "actionOutput":
            return [
              {
                value: {
                  oneofKind: "reference",
                  reference: {
                    source: {
                      oneofKind: "actionReference",
                      actionReference: pill.actionReference,
                    },
                    outputId: pill.outputId,
                  },
                } as const,
              },
            ];
          case "member":
            return [
              {
                value: {
                  oneofKind: "function",
                  function: {
                    function: {
                      oneofKind: "memberName",
                      memberName: { memberId: pill.memberId },
                    },
                  },
                } as const,
              },
            ];
          case "team":
            return [
              {
                value: {
                  oneofKind: "function",
                  function: {
                    function: {
                      oneofKind: "teamMemberIds",
                      teamMemberIds: { teamId: pill.teamId },
                    },
                  },
                } as const,
              },
            ];
          case "loop":
            if (pill.looping.source?.oneofKind === "outputReference") {
              return currentItemOutputsRefPill(pill.looping);
            }
            return [];
          default:
            return [];
        }
      })(),
    ],
  },
});

const findActionOption = (
  actionOptions: ActionOptionGroup[],
  actionId: string,
  actionInstanceId: number,
  outputId: string,
): TriggerActionOption | undefined => {
  const action = actionOptions.find(
    (action) =>
      action.action.actionReference?.actionId === actionId &&
      action.action.actionReference?.instanceId === actionInstanceId,
  );
  if (action !== undefined) {
    return action.options.find((option) => option.outputId === outputId);
  }
  return undefined;
};

export const ejectPillFromInput = (
  input: InputCandidate,
  index: number,
): InputCandidate => ({
  ...input,
  value: { values: input.value?.values.filter((_, i) => i !== index) ?? [] },
});

const loopingOutputsToOptions = (
  action: ActionCandidate,
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): TriggerActionOption[] =>
  getOutputDescriptions(action)
    .filter((output) => {
      if (isSimpleTextOutput(output)) {
        return true;
      }
      if (output.contextSpecific) {
        return systemTypeFilter(systemAllowedOptions, output);
      }

      let pass =
        output.value?.type === undefined
          ? true
          : types.includes(output.value.type);

      if (ssFieldTypes !== undefined) {
        pass =
          output.smartsuiteFieldType === undefined ||
          (ssFieldTypes.includes(output.smartsuiteFieldType as Fields) &&
            (output.value?.type === undefined ||
              types.includes(output.value.type)));
      }

      return pass;
    })
    .map((output) => ({
      title:
        output.label +
        (output.labelSuffix === undefined ? "" : `: ${output.labelSuffix}`),
      contextSpecific: output.contextSpecific,
      outputId: output.outputId,
      icon: output.icon,
      hash: action.hash,
    }));

export const loopingToOptions = (
  looping: LoopScope,
  actions: ActionCandidate[],
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): LoopingOptionGroup | undefined => {
  // If the looping source is find action
  if (
    looping.source?.oneofKind === "recordList" &&
    looping.source.recordList.recordList.oneofKind === "actionReference" &&
    looping.source.recordList.recordList.actionReference.actionId ===
      ActionLabel.findRecords
  ) {
    const actionInstanceId =
      looping.source.recordList.recordList.actionReference.instanceId;
    const sourceAction = actions.find(
      (action) => action.actionReference?.instanceId === actionInstanceId,
    );
    if (sourceAction === undefined) {
      return undefined;
    }

    return {
      name: t("general.current-item"),
      options: loopingOutputsToOptions(
        sourceAction,
        types,
        ssFieldTypes,
        systemAllowedOptions,
      ),
    };
  }

  // TODO if the looping source is array from webhook
  return undefined;
};
